import { ReactNode, useMemo, useState } from "react";
import { FolderPlus, Plus, Search, ShieldAlert } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "~/components/ui/alert";
import { Button } from "~/components/ui/button";
import { Card } from "~/components/ui/card";
import { Input } from "~/components/ui/input";
import { Separator } from "~/components/ui/separator";
import { Skeleton } from "~/components/ui/skeleton";
import useNextGenHeader from "~/contexts/nextgen-header";
import { usePageContext } from "~/contexts/page-context";
import useUser from "~/contexts/user";
import { useDocumentTitle } from "~/hooks/useDocumentTitle";
import { useFavorites } from "~/hooks/useFavorites";
import { useFetcher } from "~/hooks/useFetcher";
import { useSort, type SortOption } from "~/hooks/useSort";
import { useUserPreferences } from "~/hooks/useUserPreferences";
import { useViewMode } from "~/hooks/useViewMode";
import { NEXTGEN_MAX_CONTENT_WIDTH } from "~/lib/layout";
import { DividedList } from "~/pages/manifold-nextgen/components/DividedList";
import { ListLoadError } from "~/pages/manifold-nextgen/components/ListLoadError";
import { ListLoadingSkeleton } from "~/pages/manifold-nextgen/components/ListLoadingSkeleton";
import { ProjectViewFilter } from "~/pages/manifold-nextgen/components/ProjectViewFilter";
import { SortMenu } from "~/pages/manifold-nextgen/components/SortMenu";
import { ViewToggle } from "~/pages/manifold-nextgen/components/ViewToggle";
import { useCanAdministerProjects } from "~/pages/manifold-nextgen/hooks/useCanAdministerProjects";
import { useCanManageProjects } from "~/pages/manifold-nextgen/hooks/useCanManageProjects";
import { useProjectCollaboratorsBatch } from "~/pages/manifold-nextgen/hooks/useProjectShare";
import {
  ProjectList as ProjectListType,
  ProjectListItem,
  ProjectListView,
  projectListPath,
} from "~/services/projects";
import { NewProjectDialog } from "./NewProjectDialog";
import { ProjectCard } from "./ProjectCard";

const PROJECT_SORT_OPTIONS: SortOption<ProjectListItem>[] = [
  {
    key: "last_activity_at",
    label: "Last activity",
    kind: "date",
    accessor: (p) => Date.parse(p.last_activity_at),
    defaultDirection: "desc",
  },
  {
    key: "name",
    label: "Name",
    kind: "alpha",
    accessor: (p) => p.name,
    defaultDirection: "asc",
  },
];

export const Projects = () => {
  const user = useUser();
  const canManageProjects = useCanManageProjects();
  const canAdministerProjects = useCanAdministerProjects();
  const [preferences, updatePreferences] = useUserPreferences();
  // The views a caller's permissions can exercise, in display order: everyone
  // can use, manage/admin can narrow to owned, only admins get the bypass.
  const viewOptions = useMemo(
    () => [
      ProjectListView.USABLE,
      ...(canManageProjects || canAdministerProjects ? [ProjectListView.OWNED] : []),
      ...(canAdministerProjects ? [ProjectListView.ALL] : []),
    ],
    [canManageProjects, canAdministerProjects]
  );
  // Until `/me` resolves the capability hooks report false, so clamping the
  // saved view now would fetch `usable` for a frame on hard refresh. Trust a
  // well-formed stored view while permissions load (the backend enforces the
  // real ceiling), then clamp to what the caller can reach.
  const storedView = preferences.projectListView ?? ProjectListView.USABLE;
  const reachable = user ? viewOptions : Object.values(ProjectListView);
  const view = reachable.includes(storedView) ? storedView : ProjectListView.USABLE;

  const { data, error, isLoading } = useFetcher<ProjectListType>(projectListPath({ view }));
  // Second phase: load collaborators for the page once the list resolves, so
  // cards paint immediately and a collaborators hiccup only blanks the avatars.
  const projectIds = useMemo(() => (data?.items ?? []).map((p) => p.id), [data]);
  const collaboratorsByProject = useProjectCollaboratorsBatch(projectIds);
  const collaboratorsFor = (id: string) =>
    collaboratorsByProject ? (collaboratorsByProject[id] ?? []) : undefined;
  const [search, setSearch] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [viewMode, setViewMode] = useViewMode("nextgen-view-mode");

  const headerActions = useMemo<[ReactNode?]>(
    () =>
      canManageProjects
        ? [
            <Button
              key="new-project"
              size="sm"
              className="text-xs"
              onClick={() => setCreateOpen(true)}
            >
              <Plus aria-hidden="true" />
              New Project
            </Button>,
          ]
        : [],
    [canManageProjects]
  );

  useDocumentTitle("Projects");
  useNextGenHeader({ actions: headerActions });
  usePageContext({ page: "projects" });

  const filtered = (() => {
    if (!data?.items) {
      return [];
    }
    if (!search.trim()) {
      return data.items;
    }
    const lower = search.toLowerCase();
    return data.items.filter((p) => p.name.toLowerCase().includes(lower));
  })();

  const { sortKey, direction, activeOption, setSortKey, toggleDirection, sorted } = useSort(
    "nextgen-project-sort",
    PROJECT_SORT_OPTIONS,
    filtered
  );

  const { isFavorite, toggleFavorite, favorites, others } = useFavorites(
    "project-favorites",
    sorted,
    (p) => p.id
  );

  // While searching, the Favorites/All split is noise — show a single flat list.
  const showGroups = !search.trim() && favorites.length > 0;

  const renderProjects = (items: ProjectListItem[]) =>
    viewMode === "card" ? (
      <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4" role="list">
        {items.map((project) => (
          <li key={project.id} className="flex flex-col">
            <ProjectCard
              project={project}
              collaborators={collaboratorsFor(project.id)}
              view="card"
              favorite={isFavorite(project.id)}
              onToggleFavorite={() => toggleFavorite(project.id)}
            />
          </li>
        ))}
      </ul>
    ) : (
      <DividedList
        items={items}
        renderItem={(project) => (
          <ProjectCard
            project={project}
            collaborators={collaboratorsFor(project.id)}
            view="list"
            favorite={isFavorite(project.id)}
            onToggleFavorite={() => toggleFavorite(project.id)}
          />
        )}
      />
    );

  return (
    <>
      <div
        className="mx-auto flex w-full flex-col gap-6 px-4 py-4"
        style={{ maxWidth: NEXTGEN_MAX_CONTENT_WIDTH }}
      >
        <div className="flex items-center justify-between gap-3">
          <div className="relative w-full max-w-lg">
            <Search
              className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
              aria-hidden="true"
            />
            <Input
              type="search"
              placeholder="Search projects..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Search projects"
              className="h-8 pl-9"
            />
          </div>
          <div className="flex items-center gap-2">
            <ProjectViewFilter
              value={view}
              options={viewOptions}
              onChange={(next) => updatePreferences({ projectListView: next })}
            />
            <SortMenu
              options={PROJECT_SORT_OPTIONS}
              sortKey={sortKey}
              direction={direction}
              activeLabel={activeOption.label}
              onSelectKey={setSortKey}
              onToggleDirection={toggleDirection}
            />
            <ViewToggle value={viewMode} onChange={setViewMode} />
          </div>
        </div>

        {view === ProjectListView.ALL && (
          <Alert variant="warning" data-testid="project-admin-banner">
            <ShieldAlert />
            <AlertTitle>Admin view</AlertTitle>
            <AlertDescription>
              <p>
                You can see and manage every project in the workspace — including private ones you
                don&apos;t own. You&apos;re acting as an admin here, so tread carefully.
              </p>
            </AlertDescription>
          </Alert>
        )}

        {isLoading && viewMode === "list" && (
          <ListLoadingSkeleton
            aria-label="Loading projects"
            columns={[{ width: "w-36", height: "h-6", rounded: true }, { width: "w-36" }]}
          />
        )}
        {isLoading && viewMode === "card" && (
          <div
            role="status"
            aria-label="Loading projects"
            className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4"
          >
            {Array.from({ length: 8 }).map((_, i) => (
              <Card key={i} className="h-full min-h-80 gap-0 overflow-hidden border-l-[40px] p-0">
                <div className="flex flex-1 flex-col justify-between gap-3">
                  <div className="flex flex-col gap-3 px-5 pt-5 pb-4">
                    <Skeleton className="h-4 w-2/3" />
                    <Skeleton className="h-3 w-full" />
                    <Skeleton className="h-3 w-5/6" />
                  </div>
                  <div className="flex flex-col gap-2 px-5 py-2.5">
                    <Skeleton className="h-6 w-1/2" />
                    <Skeleton className="h-4 w-1/3" />
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

        {!isLoading && error && (
          <ListLoadError error={error} resourceLabel="projects" className="py-16" />
        )}

        {!isLoading && !error && filtered.length === 0 && (
          <div className="flex flex-col items-center gap-3 py-16 text-center">
            <FolderPlus className="size-8 text-muted-foreground" aria-hidden="true" />
            <p className="text-sm font-medium text-foreground">
              {search
                ? "No projects match your search"
                : view === ProjectListView.OWNED
                  ? "No projects you own"
                  : "No projects yet"}
            </p>
            {!search && view !== ProjectListView.OWNED && canManageProjects && (
              <p className="text-xs text-muted-foreground">
                <Button
                  variant="link"
                  className="h-auto p-0 text-xs font-normal"
                  onClick={() => setCreateOpen(true)}
                >
                  Create one
                </Button>{" "}
                to group your work.
              </p>
            )}
          </div>
        )}

        {!isLoading &&
          !error &&
          sorted.length > 0 &&
          (showGroups ? (
            <>
              <section className="flex flex-col gap-3">
                <h2 className="px-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Favorites
                </h2>
                {renderProjects(favorites)}
              </section>
              {others.length > 0 && (
                <>
                  <Separator />
                  {renderProjects(others)}
                </>
              )}
            </>
          ) : (
            renderProjects(sorted)
          ))}
      </div>

      <NewProjectDialog open={createOpen} onOpenChange={setCreateOpen} />
    </>
  );
};
